import React, { useState } from "react";
import PropTypes from "prop-types";

import ProjectDiagnosticConfig from "./ProjectDiagnostic.config";
import ProjectDiagnosticLeadForm from "./ProjectDiagnosticLeadForm";
import { getProjectDiagnosticContent } from "./projectDiagnosticContent";

import "../../../assets/stylesheets/toolBase.css";

/**
 * ProjectDiagnosticTool
 *
 * Interactive diagnostic tool that helps homeowners
 * understand the likely urgency, scope, and next step for a project.
 */
const ProjectDiagnosticTool = ({ category, zipCodeFromUrl }) => {
  const { defaults, severityLabels } = ProjectDiagnosticConfig;
  const diagnosticContent = getProjectDiagnosticContent(category);
  const questions = diagnosticContent.questions;

  const [responses, setResponses] = useState({ ...defaults });
  const [submittedAnswers, setSubmittedAnswers] = useState([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [error, setError] = useState("");
  const [severityKey, setSeverityKey] = useState("medium");
  const [showLeadForm, setShowLeadForm] = useState(false);

  const handleChange = (field, value) => {
    setResponses((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    // Simple validation: require all questions to be answered
    const unanswered = questions.filter((q) => !responses[q.id]);
    if (unanswered.length > 0) {
      setError("Please answer all questions before viewing your results.");
      return;
    }

    const answers = questions.map((q) => {
      const selectedOption = q.options.find(
        (option) => option.value === responses[q.id]
      );

      return {
        id: q.id,
        question: q.label,
        answer: selectedOption?.label || responses[q.id] || null,
        answerLabel: selectedOption?.label || null,
        answerValue: selectedOption?.value || responses[q.id] || null,
      };
    });

    const payload = {
      categoryKey: category,
      answers,
    };

    setSubmittedAnswers(answers);
    setLoading(true);
    setHasSubmitted(false);
    setSummary("");
    setSeverityKey("medium");
    setShowLeadForm(false);

    // Streaming fetch (SSE-style), similar to PropertyReportPage
    fetch("https://api.home-helper.com/api/tools/project-diagnostic", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok || !res.body) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Failed to generate report.");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";

          for (let part of parts) {
            if (!part.startsWith("data: ")) continue;
            try {
              const json = JSON.parse(part.replace("data: ", ""));

              if (json.type === "severity" && json.value) {
                setSeverityKey(json.value);
              }

              if (json.type === "content" && json.content) {
                setSummary((prev) => prev + json.content);
                setHasSubmitted(true);
              }

              if (json.type === "error" && json.message) {
                setError(json.message);
              }

              if (json.type === "done") {
                setHasSubmitted(true);
                setLoading(false);
              }
            } catch (parseErr) {
              console.error("Project Diagnostic SSE parse error", parseErr);
            }
          }
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error("Project Diagnostic error", err);
        setError(
          "We couldn’t generate a report right now. Please try again in a moment."
        );
        setLoading(false);
      });
  };

  const isReportReady = Boolean(hasSubmitted && summary && !loading);
  const leadCtaLabel = diagnosticContent.categoryLabel
    ? `Get matched with local ${diagnosticContent.categoryLabel} pros`
    : "Get matched with local pros";
  const formTitle = diagnosticContent.categoryLabel
    ? `Tell us where you need ${diagnosticContent.categoryLabel} help`
    : "Tell us where you need help";
  const formDescription = diagnosticContent.categoryLabel
    ? `Share your contact details so local ${diagnosticContent.categoryLabel} pros can review your project.`
    : "Share your contact details so local pros can review your project.";
  const successMessage = diagnosticContent.categoryLabel
    ? `Thanks — your ${diagnosticContent.categoryLabel} request has been saved.`
    : "Thanks — your project request has been saved.";
  const pageUrl =
    typeof window !== "undefined" && window.location?.href
      ? window.location.href
      : "";
  const diagnosticLeadContext = {
    categoryKey: category || diagnosticContent.requestedKey || diagnosticContent.normalizedKey,
    normalizedCategoryKey: diagnosticContent.normalizedKey,
    categoryLabel: diagnosticContent.categoryLabel,
    categoryType: diagnosticContent.categoryType,
    diagnosticTitle: diagnosticContent.title,
    diagnosticDescription: diagnosticContent.description,
    severity: severityKey,
    severityLabel: severityLabels[severityKey] || severityLabels.medium,
    summary,
    answers: submittedAnswers,
    pageUrl,
  };

  return (
    <div
      className="tool project-diagnostic-tool"
      style={{
        maxWidth: 730,
        margin: "0 auto 40px",
      }}
    >
      {/* =====================================================
          Header
         ===================================================== */}
      <header className="tool-header">
        <h1 className="tool-title">{diagnosticContent.title}</h1>
        <p className="tool-description">
          {diagnosticContent.description}
        </p>
      </header>

      {/* =====================================================
          Questions
         ===================================================== */}
      <form
        onSubmit={handleSubmit}
        style={hasSubmitted && summary ? { display: "none" } : undefined}
      >
        {questions.map((question) => (
          <div className="tool-form-row" key={question.id}>
            <label className="tool-label">{question.label}</label>

            <div className="tool-form">
              {question.options.map((option) => (
                <label key={option.value}>
                  <input
                    type="radio"
                    name={question.id}
                    value={option.value}
                    checked={responses[question.id] === option.value}
                    onChange={() =>
                      handleChange(question.id, option.value)
                    }
                  />{" "}
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="tool-actions">
          <button
            type="submit"
            className="tool-button-primary"
            disabled={loading}
          >
            {loading
              ? "Generating your report..."
              : ProjectDiagnosticConfig.ctaLabel}
          </button>
        </div>

        {error && (
          <p className="tool-muted" style={{ marginTop: 8 }}>
            {error}
          </p>
        )}
      </form>

      {/* =====================================================
          Results (AI-generated summary)
         ===================================================== */}
      {hasSubmitted && summary && (
        <section className="tool-results">
          <div className="tool-result-card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 8,
              }}
            >
              <strong>{ProjectDiagnosticConfig.result.title}</strong>

              <div
                aria-label={`Severity: ${severityKey}`}
                style={{
                  marginLeft: "auto",
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(0,0,0,0.06)",
                  backgroundColor:
                    severityKey === "low"
                      ? "#E6F7E9"
                      : severityKey === "high"
                      ? "#FDEAEA"
                      : "#FFF7E6",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  minWidth: 150,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    color:
                      severityKey === "low"
                        ? "#1A7F37"
                        : severityKey === "high"
                        ? "#D93025"
                        : "#B36B00",
                  }}
                >
                  Severity: {severityKey || "medium"}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "#555",
                    marginTop: 2,
                  }}
                >
                  {severityLabels[severityKey] || severityLabels.medium}
                </span>
              </div>
            </div>

            <p className="tool-muted" style={{ whiteSpace: "pre-line" }}>
              {summary}
            </p>
            {category && (
              <div
                style={{
                  marginTop: 16,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: 999,
                  backgroundColor: "#EEF2FF",
                  border: "1px solid #E0E7FF",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    borderRadius: "999px",
                    backgroundColor: "#4F46E5",
                    color: "#ffffff",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  i
                </span>
                <a
                  href={`/in-town/${category}`}
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#4338CA",
                    textDecoration: "none",
                  }}
                >
                  See project details and local pros
                </a>
              </div>
            )}

            {isReportReady && !showLeadForm ? (
              <div style={{ marginTop: 18 }}>
                <button
                  type="button"
                  className="tool-button-primary"
                  onClick={() => setShowLeadForm(true)}
                >
                  {leadCtaLabel}
                </button>
              </div>
            ) : null}

            {isReportReady && showLeadForm ? (
              <ProjectDiagnosticLeadForm
                ctaLabel="Send this project to local pros"
                formTitle={formTitle}
                formDescription={formDescription}
                successMessage={successMessage}
                initialZipCode={zipCodeFromUrl}
                diagnosticContext={diagnosticLeadContext}
              />
            ) : null}
          </div>
        </section>
      )}

      {hasSubmitted && (
        <p className="tool-muted" style={{ marginTop: 16 }}>
          {ProjectDiagnosticConfig.result.disclaimer}
        </p>
      )}
    </div>
  );
};

ProjectDiagnosticTool.propTypes = {
  category: PropTypes.string,
  zipCodeFromUrl: PropTypes.string,
};

export default ProjectDiagnosticTool;
